import React from "react";
import {
  LifeBuoy,
  Settings,
  Wrench,
  Smartphone,
  FileText,
  HelpCircle,
  ShieldCheck,
  Phone,
  Mail,
} from "lucide-react";

export default function SupportCentre() {
  return (
    <div className="bg-gray-50 text-gray-800">

      {/* ================= HERO SECTION ================= */}
      <section className="bg-blue-900 text-white py-20 px-6 text-center">
        <div className="max-w-4xl mx-auto">
          <LifeBuoy className="mx-auto mb-6 w-14 h-14 text-blue-300" />
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            24/7 CCTV Support & Technical Assistance
          </h1>
          <p className="text-lg md:text-xl text-blue-100 mb-8">
            Get fast, reliable, and expert support for installation,
            troubleshooting, warranty, and product guidance.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button className="bg-white text-blue-900 px-6 py-3 rounded-lg font-semibold hover:bg-gray-200 transition">
              Raise Support Ticket
            </button>
            <button className="border border-white px-6 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-900 transition">
              Live Chat Support
            </button>
          </div>
        </div>
      </section>

      {/* ================= INTRO SECTION ================= */}
      <section className="py-16 px-6 max-w-6xl mx-auto text-center">
        <h2 className="text-3xl font-bold mb-6">Welcome to Our Support Centre</h2>
        <p className="text-gray-600 text-lg leading-relaxed">
          We provide complete technical support for CCTV systems including 
          installation guidance, troubleshooting, software assistance, warranty 
          information, and maintenance help. Our certified technicians ensure 
          your surveillance system operates smoothly and securely at all times.
        </p>
      </section>

      {/* ================= SUPPORT CATEGORIES ================= */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-8">

          {/* Installation Support */}
          <div className="bg-gray-50 p-8 rounded-xl shadow hover:shadow-xl transition">
            <Settings className="w-12 h-12 text-blue-900 mb-4" />
            <h3 className="text-xl font-bold mb-4">Installation Support</h3>
            <ul className="text-gray-600 space-y-2">
              <li>• Camera installation guidance</li>
              <li>• DVR/NVR configuration</li>
              <li>• Remote mobile setup</li>
              <li>• Network & WiFi configuration</li>
              <li>• Motion alert setup</li>
            </ul>
          </div>

          {/* Troubleshooting */}
          <div className="bg-gray-50 p-8 rounded-xl shadow hover:shadow-xl transition">
            <Wrench className="w-12 h-12 text-blue-900 mb-4" />
            <h3 className="text-xl font-bold mb-4">Troubleshooting</h3>
            <ul className="text-gray-600 space-y-2">
              <li>• No video signal</li>
              <li>• Camera offline issue</li>
              <li>• DVR not recording</li>
              <li>• Hard disk errors</li>
              <li>• Password reset help</li>
            </ul>
          </div>

          {/* App Support */}
          <div className="bg-gray-50 p-8 rounded-xl shadow hover:shadow-xl transition">
            <Smartphone className="w-12 h-12 text-blue-900 mb-4" />
            <h3 className="text-xl font-bold mb-4">Software & App Support</h3>
            <ul className="text-gray-600 space-y-2">
              <li>• Mobile app setup</li>
              <li>• Playback issues</li>
              <li>• Firmware updates</li>
              <li>• Cloud configuration</li>
              <li>• Remote access errors</li>
            </ul>
          </div>

        </div>
      </section>

      {/* ================= KNOWLEDGE BASE ================= */}
      <section className="py-16 px-6 bg-gray-100">
        <div className="max-w-5xl mx-auto text-center">
          <FileText className="mx-auto w-12 h-12 text-blue-900 mb-4" />
          <h2 className="text-3xl font-bold mb-6">Knowledge Base & Guides</h2>
          <div className="grid md:grid-cols-2 gap-6 text-left">
            <div className="bg-white p-6 rounded-lg shadow">
              How to Reset DVR/NVR Password?
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              How to Connect CCTV to Mobile?
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              How to Backup CCTV Footage?
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              How to Fix Camera Offline Issue?
            </div>
          </div>
        </div>
      </section>

      {/* ================= SUPPORT FORM ================= */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-10">
            Raise a Support Ticket
          </h2>

          <form className="grid md:grid-cols-2 gap-6">
            <input
              type="text"
              placeholder="Full Name"
              className="border p-3 rounded-lg"
            />
            <input
              type="text"
              placeholder="Contact Number"
              className="border p-3 rounded-lg"
            />
            <input
              type="email"
              placeholder="Email Address"
              className="border p-3 rounded-lg"
            />
            <input
              type="text"
              placeholder="Product Model"
              className="border p-3 rounded-lg"
            />
            <textarea
              placeholder="Describe Your Issue"
              className="border p-3 rounded-lg md:col-span-2"
              rows="4"
            ></textarea>

            <button className="md:col-span-2 bg-blue-900 text-white py-3 rounded-lg font-semibold hover:bg-blue-800 transition">
              Submit Ticket
            </button>
          </form>
        </div>
      </section>

      {/* ================= WARRANTY & AMC ================= */}
      <section className="py-16 px-6 bg-gray-100">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10">

          <div>
            <ShieldCheck className="w-12 h-12 text-blue-900 mb-4" />
            <h3 className="text-2xl font-bold mb-4">Warranty Information</h3>
            <p className="text-gray-600 leading-relaxed">
              We provide manufacturer-backed warranty coverage along with
              3–6 months service warranty on repairs. Genuine spare parts
              and transparent service process guaranteed.
            </p>
          </div>

          <div>
            <HelpCircle className="w-12 h-12 text-blue-900 mb-4" />
            <h3 className="text-2xl font-bold mb-4">Annual Maintenance Contract</h3>
            <p className="text-gray-600 leading-relaxed">
              Choose from Basic, Standard, or Premium AMC plans to ensure
              long-term performance and priority support for your CCTV systems.
            </p>
          </div>

        </div>
      </section>

      {/* ================= CONTACT SUPPORT ================= */}
      <section className="bg-blue-900 text-white py-16 px-6 text-center">
        <h2 className="text-3xl font-bold mb-6">Need Immediate Help?</h2>
        <div className="flex flex-col md:flex-row justify-center gap-8">
          <div className="flex items-center gap-3 justify-center">
            <Phone />
            <span>+91 XXXXX XXXXX</span>
          </div>
          <div className="flex items-center gap-3 justify-center">
            <Mail />
            <span>support@yourcompany.com</span>
          </div>
        </div>
      </section>

    </div>
  );
}